import { useState, useRef, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Upload, Trash2, X, Download, Archive, Clock, ChevronDown, ChevronRight, Plus, Building2, FolderOpen, Folder, Shield, ClipboardCheck, AlertTriangle, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Recursively read all files from a directory entry
const readAllEntries = async (entry: FileSystemEntry): Promise<File[]> => {
  if (entry.isFile) {
    return new Promise((resolve) => {
      (entry as FileSystemFileEntry).file((f) => {
        Object.defineProperty(f, 'fullPath', { value: entry.fullPath, writable: false });
        resolve([f]);
      });
    });
  }
  if (entry.isDirectory) {
    const dirReader = (entry as FileSystemDirectoryEntry).createReader();
    const entries: FileSystemEntry[] = [];
    let batch: FileSystemEntry[];
    do {
      batch = await new Promise((resolve) => dirReader.readEntries((e) => resolve(e)));
      entries.push(...batch);
    } while (batch.length > 0);
    const fileArrays = await Promise.all(entries.map(readAllEntries));
    return fileArrays.flat();
  }
  return [];
};

interface PendingFile {
  file: File;
  name: string;
  description: string;
  version: string;
  parentDocumentId: string | null;
  category: string;
}

const CLIENT_DOC_CATEGORIES = [
  { value: "project_documents", label: "Project Documents" },
  { value: "voff_digests", label: "VOFF Digests" },
];

const OPERATING_CATEGORIES = [
  "Class and Flag",
  "Corporate Governance",
  "DP Governance",
  "General Ops",
  "HR",
  "Operational Excellence",
  "QMS",
  "SMS",
  "Supply Chain",
  "Vessel Checklist",
];

const DOCUMENT_TYPES = [
  { value: "operating_document", label: "Operating Document", icon: "folder", description: "SMS, CONOPS, procedures, policies" },
  { value: "safety_bulletin", label: "Safety / Security Bulletin", icon: "alert", description: "Safety alerts, security notices, incident reports" },
  { value: "announcement", label: "Company News / Announcement", icon: "megaphone", description: "Company updates, news, internal communications" },
  { value: "onboarding_material", label: "Onboarding Material", icon: "folder", description: "New hire training materials, orientation docs" },
  { value: "training_document", label: "Training / Certification Doc", icon: "folder", description: "Certifications, training guides, competency records" },
];

const DISPLAY_SECTIONS = [
  { value: "company_documents", label: "Company Documents" },
  { value: "sms_reviews", label: "SMS Reviews" },
  { value: "client_documents", label: "Client Documents" },
];

const BULLETIN_CATEGORIES = [
  "Safety",
  "Security",
  "Environmental",
  "Operational",
  "General",
];

const BULLETIN_SEVERITIES = [
  { value: "critical", label: "Critical" },
  { value: "warning", label: "Warning" },
  { value: "info", label: "Informational" },
];

const ANNOUNCEMENT_PRIORITIES = [
  { value: "urgent", label: "Urgent" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];

const EMPLOYEE_ROLES = [
  { value: "", label: "All Employees (No Restriction)" },
  { value: "executive_team", label: "Executive Team" },
  { value: "shoreside_leadership", label: "Shoreside Leadership" },
  { value: "shoreside_employee", label: "Shoreside Employee" },
  { value: "captain", label: "Captain" },
  { value: "crew", label: "Crew" },
  { value: "engineer", label: "Engineer" },
  { value: "manager", label: "Manager" },
  { value: "office", label: "Office" },
  { value: "hse", label: "HSE" },
];

const getRoleLabel = (role: string | null) => {
  if (!role) return "All Employees";
  return EMPLOYEE_ROLES.find((r) => r.value === role)?.label || role;
};

const AdminDocuments = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const opFileRef = useRef<HTMLInputElement>(null);
  const opFolderRef = useRef<HTMLInputElement>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);

  // Operating docs state
  const [activeTab, setActiveTab] = useState<"client" | "operating">("client");
  const [showOpForm, setShowOpForm] = useState(false);
  const [opPendingFiles, setOpPendingFiles] = useState<{ file: File; name: string; description: string; category: string; version: string; required_role: string; is_sms_review: boolean; display_section: string; document_type: string; severity: string; priority: string; is_pinned: boolean }[]>([]);
  const [opUploading, setOpUploading] = useState(false);
  const [opDragging, setOpDragging] = useState(false);

  const { data: companies = [] } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => {
      const { data } = await supabase.from("companies").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["admin-documents"],
    queryFn: async () => {
      const { data } = await supabase
        .from("documents")
        .select("*, companies(name)")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: operatingDocs = [], isLoading: opLoading } = useQuery({
    queryKey: ["admin-operating-documents"],
    queryFn: async () => {
      const { data } = await supabase
        .from("operating_documents")
        .select("*")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const activeDocuments = documents.filter((d) => !d.is_archived && !d.parent_document_id);
  const archivedDocuments = documents.filter((d) => d.is_archived);
  const activeOpDocs = operatingDocs.filter((d) => !d.is_archived);
  const archivedOpDocs = operatingDocs.filter((d) => d.is_archived);

  const getVersionHistory = (parentId: string) =>
    documents
      .filter((d) => d.parent_document_id === parentId)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const stripExtension = (filename: string) => {
    const lastDot = filename.lastIndexOf(".");
    return lastDot > 0 ? filename.substring(0, lastDot) : filename;
  };

  const [dragging, setDragging] = useState(false);

  const addFiles = useCallback((files: File[]) => {
    const newPending: PendingFile[] = files.map((f) => ({
      file: f,
      name: stripExtension(f.name),
      description: "",
      version: "",
      parentDocumentId: null,
      category: "project_documents",
    }));
    setPendingFiles((prev) => [...prev, ...newPending]);
  }, []);

  const addOpFiles = useCallback((files: File[]) => {
    const newPending = files.map((f) => {
      const relativePath = (f as any).fullPath || (f as any).webkitRelativePath || "";
      const pathParts = relativePath.replace(/^\//, "").split("/");
      const folderPath = pathParts.length > 1 ? pathParts.slice(0, -1).join("/") : "";
      const displayName = folderPath ? `${folderPath}/${stripExtension(f.name)}` : stripExtension(f.name);
      const topFolder = pathParts.length > 1 ? pathParts[0] : "";
      const matchedCategory = OPERATING_CATEGORIES.find(
        (cat) => cat.toLowerCase().includes(topFolder.toLowerCase()) || topFolder.toLowerCase().includes(cat.split(" ")[0].toLowerCase())
      );
      return {
        file: f,
        name: displayName,
        description: folderPath ? `Folder: ${folderPath}` : "",
        category: matchedCategory || "General",
        version: "",
        required_role: "",
        is_sms_review: false,
        display_section: "company_documents",
        document_type: "operating_document",
        severity: "info",
        priority: "normal",
        is_pinned: false,
      };
    });
    setOpPendingFiles((prev) => [...prev, ...newPending]);
  }, []);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleOpDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setOpDragging(false);
    const items = e.dataTransfer.items;
    if (items) {
      const allFiles: File[] = [];
      const entries: FileSystemEntry[] = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }
      for (const entry of entries) {
        const files = await readAllEntries(entry);
        allFiles.push(...files);
      }
      if (allFiles.length > 0) {
        addOpFiles(allFiles);
        return;
      }
    }
    addOpFiles(Array.from(e.dataTransfer.files));
  }, [addOpFiles]);

  const updatePending = (idx: number, updates: Partial<PendingFile>) => {
    setPendingFiles((prev) => prev.map((p, i) => (i === idx ? { ...p, ...updates } : p)));
  };

  const removePending = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingFiles.length === 0 || !selectedCompany) return;
    setUploading(true);
    let successCount = 0;

    try {
      for (const pf of pendingFiles) {
        const ext = pf.file.name.split(".").pop();
        const path = `${selectedCompany}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

        const { error: storageErr } = await supabase.storage
          .from("client-documents")
          .upload(path, pf.file);
        if (storageErr) throw storageErr;

        const insertData: Record<string, unknown> = {
          company_id: selectedCompany,
          name: pf.name.trim() || stripExtension(pf.file.name),
          description: pf.description.trim() || null,
          file_path: path,
          uploaded_by: user!.id,
          version: pf.version.trim() || null,
          parent_document_id: pf.parentDocumentId || null,
          is_archived: false,
          category: pf.category,
        };

        const { error: dbErr } = await supabase.from("documents").insert(insertData as any);
        if (dbErr) throw dbErr;
        successCount++;
      }

      const docNames = pendingFiles.map((pf) => pf.name.trim() || stripExtension(pf.file.name));
      await supabase.from("notifications").insert({
        company_id: selectedCompany,
        type: "document",
        title: successCount > 1
          ? `${successCount} new documents uploaded`
          : `New document: ${docNames[0]}`,
        message: successCount > 1
          ? `Documents uploaded: ${docNames.join(", ")}`
          : docNames[0],
      });

      toast.success(`${successCount} document${successCount > 1 ? "s" : ""} uploaded`);
      setShowForm(false);
      setPendingFiles([]);
      setSelectedCompany("");
      qc.invalidateQueries({ queryKey: ["admin-documents"] });
      qc.invalidateQueries({ queryKey: ["admin-doc-count"] });
    } catch {
      toast.error(`Upload failed after ${successCount} file${successCount !== 1 ? "s" : ""}`);
    } finally {
      setUploading(false);
    }
  };

  // Operating doc upload
  const handleOpUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (opPendingFiles.length === 0) return;
    setOpUploading(true);
    let successCount = 0;

    try {
      for (const pf of opPendingFiles) {
        const ext = pf.file.name.split(".").pop();
        const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

        if (pf.document_type === "safety_bulletin") {
          // Upload to operating-documents bucket, insert into safety_bulletins table
          const { error: storageErr } = await supabase.storage
            .from("operating-documents")
            .upload(path, pf.file);
          if (storageErr) throw storageErr;

          const { error: dbErr } = await supabase.from("safety_bulletins").insert({
            title: pf.name.trim() || stripExtension(pf.file.name),
            content: pf.description.trim() || "See attached document",
            category: pf.category || "General",
            severity: pf.severity || "info",
            file_path: path,
            author_id: user!.id,
            is_active: true,
          } as any);
          if (dbErr) throw dbErr;
        } else if (pf.document_type === "announcement") {
          // No file upload needed for announcements, but store in operating-documents if file attached
          const { error: storageErr } = await supabase.storage
            .from("operating-documents")
            .upload(path, pf.file);
          if (storageErr) throw storageErr;

          const { error: dbErr } = await supabase.from("announcements").insert({
            title: pf.name.trim() || stripExtension(pf.file.name),
            content: pf.description.trim() || "See attached document: " + path,
            priority: pf.priority || "normal",
            is_pinned: pf.is_pinned,
            author_id: user!.id,
          } as any);
          if (dbErr) throw dbErr;
        } else if (pf.document_type === "onboarding_material") {
          const { error: storageErr } = await supabase.storage
            .from("operating-documents")
            .upload(path, pf.file);
          if (storageErr) throw storageErr;

          const { error: dbErr } = await supabase.from("onboarding_materials").insert({
            title: pf.name.trim() || stripExtension(pf.file.name),
            description: pf.description.trim() || null,
            category: pf.category || "general",
            file_path: path,
            is_active: true,
          } as any);
          if (dbErr) throw dbErr;
        } else if (pf.document_type === "training_document") {
          // Training docs are stored in operating-documents bucket, inserted into operating_documents table with training category
          const { error: storageErr } = await supabase.storage
            .from("operating-documents")
            .upload(path, pf.file);
          if (storageErr) throw storageErr;

          const { error: dbErr } = await supabase.from("operating_documents").insert({
            name: pf.name.trim() || stripExtension(pf.file.name),
            description: pf.description.trim() || null,
            category: "Training",
            file_path: path,
            uploaded_by: user!.id,
            version: pf.version.trim() || null,
            required_role: pf.required_role || null,
            display_section: "company_documents",
          } as any);
          if (dbErr) throw dbErr;
        } else {
          // Operating document (default)
          const { error: storageErr } = await supabase.storage
            .from("operating-documents")
            .upload(path, pf.file);
          if (storageErr) throw storageErr;

          const { error: dbErr } = await supabase.from("operating_documents").insert({
            name: pf.name.trim() || stripExtension(pf.file.name),
            description: pf.description.trim() || null,
            category: pf.category,
            file_path: path,
            uploaded_by: user!.id,
            version: pf.version.trim() || null,
            required_role: pf.required_role || null,
            is_sms_review: pf.is_sms_review || pf.display_section === "sms_reviews",
            display_section: pf.display_section,
          } as any);
          if (dbErr) throw dbErr;
        }
        successCount++;
      }

      toast.success(`${successCount} item${successCount > 1 ? "s" : ""} uploaded successfully`);
      setShowOpForm(false);
      setOpPendingFiles([]);
      qc.invalidateQueries({ queryKey: ["admin-operating-documents"] });
      qc.invalidateQueries({ queryKey: ["safety-bulletins"] });
      qc.invalidateQueries({ queryKey: ["announcements"] });
      qc.invalidateQueries({ queryKey: ["onboarding-materials"] });
    } catch {
      toast.error(`Upload failed after ${successCount} file${successCount !== 1 ? "s" : ""}`);
    } finally {
      setOpUploading(false);
    }
  };

  const archiveDoc = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("documents").update({ is_archived: true } as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-documents"] }); toast.success("Document archived"); },
    onError: () => toast.error("Failed to archive"),
  });

  const restoreDoc = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("documents").update({ is_archived: false } as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-documents"] }); toast.success("Document restored"); },
    onError: () => toast.error("Failed to restore"),
  });

  const archiveOpDoc = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("operating_documents").update({ is_archived: true } as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-operating-documents"] }); toast.success("Document archived"); },
    onError: () => toast.error("Failed to archive"),
  });

  const restoreOpDoc = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("operating_documents").update({ is_archived: false } as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-operating-documents"] }); toast.success("Document restored"); },
    onError: () => toast.error("Failed to restore"),
  });

  const deleteDoc = useMutation({
    mutationFn: async ({ id, filePath }: { id: string; filePath: string }) => {
      await supabase.storage.from("client-documents").remove([filePath]);
      const { error } = await supabase.from("documents").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-documents"] }); qc.invalidateQueries({ queryKey: ["admin-doc-count"] }); toast.success("Document deleted"); },
    onError: () => toast.error("Failed to delete"),
  });

  const deleteOpDoc = useMutation({
    mutationFn: async ({ id, filePath }: { id: string; filePath: string }) => {
      await supabase.storage.from("operating-documents").remove([filePath]);
      const { error } = await supabase.from("operating_documents").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-operating-documents"] }); toast.success("Document deleted"); },
    onError: () => toast.error("Failed to delete"),
  });

  const handleDownload = async (filePath: string, fileName: string, bucket = "client-documents") => {
    const { data } = await supabase.storage.from(bucket).createSignedUrl(filePath, 60);
    if (data?.signedUrl) {
      const a = document.createElement("a");
      a.href = data.signedUrl;
      a.download = fileName;
      a.click();
    }
  };

  const DocRow = ({ doc, showRestore = false }: { doc: any; showRestore?: boolean }) => {
    const versions = getVersionHistory(doc.id);
    const isExpanded = expandedDoc === doc.id;

    return (
      <div>
        <div className="glass-card rounded-xl p-5 flex items-center gap-4">
          {versions.length > 0 && (
            <button
              onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
              className="p-1 text-primary-foreground/30 hover:text-primary-foreground/60 transition-colors"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-primary-foreground/80 font-body font-medium truncate">{doc.name}</p>
            <p className="text-xs text-primary-foreground/30 font-body mt-0.5 flex flex-wrap items-center gap-1.5">
              <span>{(doc.companies as { name: string } | null)?.name ?? "—"} · {new Date(doc.created_at).toLocaleDateString()}</span>
              {doc.category === "voff_digests" && (
                <span className="px-2 py-0.5 rounded-md bg-teal/10 text-teal text-[10px] font-bold">VOFF Digest</span>
              )}
              {doc.version && (
                <span className="ml-1 px-2 py-0.5 rounded-md bg-teal/10 text-teal text-[10px] font-bold">
                  v{doc.version}
                </span>
              )}
              {versions.length > 0 && (
                <span className="ml-2 text-primary-foreground/20">· {versions.length} previous version{versions.length > 1 ? "s" : ""}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleDownload(doc.file_path, doc.name)} className="p-2 rounded-lg bg-teal/10 text-teal hover:bg-teal/20 transition-all" title="Download"><Download className="w-4 h-4" /></button>
            {showRestore ? (
              <button onClick={() => restoreDoc.mutate(doc.id)} className="p-2 rounded-lg bg-teal/10 text-teal hover:bg-teal/20 transition-all" title="Restore"><Clock className="w-4 h-4" /></button>
            ) : (
              <button onClick={() => archiveDoc.mutate(doc.id)} className="p-2 rounded-lg text-primary-foreground/20 hover:text-amber-400 hover:bg-amber-400/5 transition-all" title="Archive"><Archive className="w-4 h-4" /></button>
            )}
            <button onClick={() => { if (confirm(`Delete "${doc.name}"?`)) deleteDoc.mutate({ id: doc.id, filePath: doc.file_path }); }} className="p-2 rounded-lg text-primary-foreground/20 hover:text-destructive hover:bg-destructive/5 transition-all" title="Delete"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>

        {isExpanded && versions.length > 0 && (
          <div className="ml-10 mt-1 space-y-1">
            {versions.map((v) => (
              <div key={v.id} className="glass-card rounded-lg p-3 flex items-center gap-3 opacity-70">
                <Clock className="w-3.5 h-3.5 text-primary-foreground/25 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-primary-foreground/50 font-body truncate">
                    {v.name}
                    {v.version && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-primary-foreground/5 text-[10px]">v{v.version}</span>
                    )}
                  </p>
                  <p className="text-[10px] text-primary-foreground/20 font-body">{new Date(v.created_at).toLocaleDateString()}</p>
                </div>
                <button onClick={() => handleDownload(v.file_path, v.name)} className="p-1.5 rounded-lg bg-teal/10 text-teal hover:bg-teal/20 transition-all"><Download className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const OpDocRow = ({ doc, showRestore = false }: { doc: any; showRestore?: boolean }) => (
    <div className="glass-card rounded-xl p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
        {(doc as any).is_sms_review ? <ClipboardCheck className="w-5 h-5 text-teal" /> : <FolderOpen className="w-5 h-5 text-teal" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-primary-foreground/80 font-body font-medium truncate">{doc.name}</p>
        <p className="text-xs text-primary-foreground/30 font-body mt-0.5 flex flex-wrap items-center gap-1.5">
          <span className="px-2 py-0.5 rounded-md bg-primary-foreground/5 text-primary-foreground/40 text-[10px] font-bold">{doc.category}</span>
          {(doc as any).required_role && (
            <span className="px-2 py-0.5 rounded-md bg-amber-400/10 text-amber-400 text-[10px] font-bold flex items-center gap-1">
              <Shield className="w-3 h-3" />
              {getRoleLabel((doc as any).required_role)}
            </span>
          )}
          {(doc as any).is_sms_review && (
            <span className="px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 text-[10px] font-bold">SMS Review</span>
          )}
          {(doc as any).display_section && (doc as any).display_section !== "company_documents" && (
            <span className="px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 text-[10px] font-bold">
              {DISPLAY_SECTIONS.find((s) => s.value === (doc as any).display_section)?.label || (doc as any).display_section}
            </span>
          )}
          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
          {doc.version && (
            <span className="px-2 py-0.5 rounded-md bg-teal/10 text-teal text-[10px] font-bold">v{doc.version}</span>
          )}
        </p>
        {doc.description && <p className="text-xs text-primary-foreground/25 font-body mt-1 truncate">{doc.description}</p>}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => handleDownload(doc.file_path, doc.name, "operating-documents")} className="p-2 rounded-lg bg-teal/10 text-teal hover:bg-teal/20 transition-all" title="Download"><Download className="w-4 h-4" /></button>
        {showRestore ? (
          <button onClick={() => restoreOpDoc.mutate(doc.id)} className="p-2 rounded-lg bg-teal/10 text-teal hover:bg-teal/20 transition-all" title="Restore"><Clock className="w-4 h-4" /></button>
        ) : (
          <button onClick={() => archiveOpDoc.mutate(doc.id)} className="p-2 rounded-lg text-primary-foreground/20 hover:text-amber-400 hover:bg-amber-400/5 transition-all" title="Archive"><Archive className="w-4 h-4" /></button>
        )}
        <button onClick={() => { if (confirm(`Delete "${doc.name}"?`)) deleteOpDoc.mutate({ id: doc.id, filePath: doc.file_path }); }} className="p-2 rounded-lg text-primary-foreground/20 hover:text-destructive hover:bg-destructive/5 transition-all" title="Delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
          <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
            Document <span className="accent-text">Management</span>
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-primary-foreground/5">
        <button
          onClick={() => setActiveTab("client")}
          className={`flex items-center gap-2 px-5 py-3 text-[13px] font-body font-medium tracking-wide border-b-2 transition-all ${
            activeTab === "client" ? "border-teal text-teal" : "border-transparent text-primary-foreground/40 hover:text-primary-foreground/70"
          }`}
        >
          <Building2 className="w-4 h-4" />
          Client Documents
        </button>
        <button
          onClick={() => setActiveTab("operating")}
          className={`flex items-center gap-2 px-5 py-3 text-[13px] font-body font-medium tracking-wide border-b-2 transition-all ${
            activeTab === "operating" ? "border-teal text-teal" : "border-transparent text-primary-foreground/40 hover:text-primary-foreground/70"
          }`}
        >
          <FolderOpen className="w-4 h-4" />
          Company Documents & Content
        </button>
      </div>

      {/* ========== CLIENT DOCUMENTS TAB ========== */}
      {activeTab === "client" && (
        <>
          <div className="flex justify-end mb-6">
            <button
              onClick={() => { setShowForm(!showForm); if (showForm) setPendingFiles([]); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body"
            >
              {showForm ? <X className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
              {showForm ? "Cancel" : "Upload Documents"}
            </button>
          </div>

          {showForm && (
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h2 className="font-display text-lg text-primary-foreground mb-4">Batch Upload Documents</h2>
              <form onSubmit={handleUpload} className="space-y-4">
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm focus:outline-none focus:border-teal/40 transition-all"
                >
                  <option value="">Select company...</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${dragging ? "border-teal bg-teal/5" : "border-primary-foreground/10 hover:border-teal/30"}`}
                >
                  <Upload className={`w-6 h-6 mx-auto mb-2 ${dragging ? "text-teal" : "text-primary-foreground/30"}`} />
                  <p className="text-sm font-body text-primary-foreground/50">
                    {dragging ? "Drop files here" : "Drag & drop files here, or click to browse"}
                  </p>
                  <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFilesSelected} />
                </div>

                {pendingFiles.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs text-primary-foreground/40 font-body font-bold uppercase tracking-wider">
                      {pendingFiles.length} file{pendingFiles.length > 1 ? "s" : ""} selected
                    </p>
                    {pendingFiles.map((pf, idx) => (
                      <div key={idx} className="bg-primary-foreground/5 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-teal flex-shrink-0" />
                            <span className="text-xs text-primary-foreground/40 font-body truncate">{pf.file.name}</span>
                          </div>
                          <button type="button" onClick={() => removePending(idx)} className="p-1 text-primary-foreground/20 hover:text-destructive transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <input type="text" value={pf.name} onChange={(e) => updatePending(idx, { name: e.target.value })} placeholder="Document name" className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
                          <input type="text" value={pf.description} onChange={(e) => updatePending(idx, { description: e.target.value })} placeholder="Description (optional)" className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
                          <input type="text" value={pf.version} onChange={(e) => updatePending(idx, { version: e.target.value })} placeholder="Version (e.g. 1.0)" className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
                          <select
                            value={pf.category}
                            onChange={(e) => updatePending(idx, { category: e.target.value })}
                            className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all"
                          >
                            {CLIENT_DOC_CATEGORIES.map((c) => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                        <select
                          value={pf.parentDocumentId ?? ""}
                          onChange={(e) => updatePending(idx, { parentDocumentId: e.target.value || null })}
                          className="w-full px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all"
                        >
                          <option value="">New document (not a version update)</option>
                          {activeDocuments.map((d) => (
                            <option key={d.id} value={d.id}>Update version of: {d.name} {d.version ? `(v${d.version})` : ""}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={uploading || pendingFiles.length === 0}
                  className="px-6 py-3 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body disabled:opacity-50"
                >
                  {uploading ? "Uploading..." : `Upload ${pendingFiles.length} Document${pendingFiles.length > 1 ? "s" : ""}`}
                </button>
              </form>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
          ) : (
            <>
              {activeDocuments.length === 0 ? (
                <div className="glass-card rounded-2xl p-16 text-center">
                  <FileText className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
                  <p className="text-primary-foreground/40 font-body">No client documents uploaded yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeDocuments.map((doc) => <DocRow key={doc.id} doc={doc} />)}
                </div>
              )}

              <div className="mt-10">
                <button onClick={() => setShowArchive(!showArchive)} className="flex items-center gap-2 text-primary-foreground/40 hover:text-primary-foreground/60 font-body text-sm transition-colors mb-4">
                  <Archive className="w-4 h-4" />
                  Archive ({archivedDocuments.length})
                  {showArchive ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
                {showArchive && archivedDocuments.length > 0 && (
                  <div className="space-y-3 opacity-60">{archivedDocuments.map((doc) => <DocRow key={doc.id} doc={doc} showRestore />)}</div>
                )}
                {showArchive && archivedDocuments.length === 0 && (
                  <p className="text-xs text-primary-foreground/20 font-body ml-6">No archived documents.</p>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ========== OPERATING DOCUMENTS TAB ========== */}
      {activeTab === "operating" && (
        <>
          <div className="flex justify-between items-start mb-6">
            <p className="text-sm text-primary-foreground/40 font-body max-w-lg">
              Upload and categorize content as operating documents, safety/security bulletins, or company announcements. Each type routes to the appropriate section for employees.
            </p>
            <button
              onClick={() => { setShowOpForm(!showOpForm); if (showOpForm) setOpPendingFiles([]); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body flex-shrink-0"
            >
              {showOpForm ? <X className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
              {showOpForm ? "Cancel" : "Upload Documents"}
            </button>
          </div>

          {showOpForm && (
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h2 className="font-display text-lg text-primary-foreground mb-4">Upload Operating Documents</h2>
              <form onSubmit={handleOpUpload} className="space-y-4">
                <div
                  onDragOver={(e) => { e.preventDefault(); setOpDragging(true); }}
                  onDragLeave={() => setOpDragging(false)}
                  onDrop={handleOpDrop}
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${opDragging ? "border-teal bg-teal/5" : "border-primary-foreground/10 hover:border-teal/30"}`}
                >
                  <Upload className={`w-6 h-6 mx-auto mb-2 ${opDragging ? "text-teal" : "text-primary-foreground/30"}`} />
                  <p className="text-sm font-body text-primary-foreground/50 mb-3">
                    {opDragging ? "Drop files or folders here" : "Drag & drop files or folders here"}
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button type="button" onClick={() => opFileRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground/60 font-body text-xs hover:border-teal/30 hover:text-primary-foreground/80 transition-all">
                      <FileText className="w-3.5 h-3.5" />Browse Files
                    </button>
                    <button type="button" onClick={() => opFolderRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground/60 font-body text-xs hover:border-teal/30 hover:text-primary-foreground/80 transition-all">
                      <Folder className="w-3.5 h-3.5" />Browse Folder
                    </button>
                  </div>
                  <input ref={opFileRef} type="file" multiple className="hidden" onChange={(e) => { addOpFiles(Array.from(e.target.files ?? [])); if (opFileRef.current) opFileRef.current.value = ""; }} />
                  <input
                    ref={opFolderRef}
                    type="file"
                    multiple
                    className="hidden"
                    {...({ webkitdirectory: "", directory: "" } as any)}
                    onChange={(e) => { addOpFiles(Array.from(e.target.files ?? [])); if (opFolderRef.current) opFolderRef.current.value = ""; }}
                  />
                </div>

                {opPendingFiles.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-primary-foreground/40 font-body font-bold uppercase tracking-wider">
                        {opPendingFiles.length} file{opPendingFiles.length > 1 ? "s" : ""} selected
                      </p>
                      {/* Bulk role assignment */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-primary-foreground/30 font-body">Set all to:</span>
                        <select
                          onChange={(e) => {
                            const role = e.target.value;
                            setOpPendingFiles((prev) => prev.map((p) => ({ ...p, required_role: role })));
                          }}
                          className="px-2 py-1 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-[10px] focus:outline-none focus:border-teal/40 transition-all"
                        >
                          {EMPLOYEE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </div>
                    </div>
                    {opPendingFiles.map((pf, idx) => (
                      <div key={idx} className="bg-primary-foreground/5 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            {pf.document_type === "safety_bulletin" ? <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" /> :
                             pf.document_type === "announcement" ? <Megaphone className="w-4 h-4 text-blue-400 flex-shrink-0" /> :
                             <FolderOpen className="w-4 h-4 text-teal flex-shrink-0" />}
                            <span className="text-xs text-primary-foreground/40 font-body truncate">{pf.file.name}</span>
                          </div>
                          <button type="button" onClick={() => setOpPendingFiles((prev) => prev.filter((_, i) => i !== idx))} className="p-1 text-primary-foreground/20 hover:text-destructive transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Document Type Selector */}
                        <div>
                          <label className="text-[10px] text-primary-foreground/30 font-body mb-1 block font-bold uppercase tracking-wider">Document Type</label>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                            {DOCUMENT_TYPES.map((dt) => (
                              <button
                                key={dt.value}
                                type="button"
                                onClick={() => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, document_type: dt.value, category: dt.value === "safety_bulletin" ? "Safety" : dt.value === "announcement" ? "General" : p.category } : p))}
                                className={`px-3 py-2.5 rounded-lg border text-left transition-all ${
                                  pf.document_type === dt.value
                                    ? "border-teal bg-teal/10 text-teal"
                                    : "border-primary-foreground/10 text-primary-foreground/50 hover:border-primary-foreground/20"
                                }`}
                              >
                                <span className="text-xs font-body font-medium block">{dt.label}</span>
                                <span className="text-[10px] font-body opacity-60">{dt.description}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Common fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input type="text" value={pf.name} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, name: e.target.value } : p))} placeholder="Title / Name" className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
                          <input type="text" value={pf.description} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, description: e.target.value } : p))} placeholder="Description (optional)" className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
                        </div>

                        {/* Operating Document fields */}
                        {pf.document_type === "operating_document" && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <select value={pf.category} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, category: e.target.value } : p))} className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                                {OPERATING_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <select value={pf.required_role} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, required_role: e.target.value } : p))} className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                                {EMPLOYEE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                              </select>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <input type="text" value={pf.version} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, version: e.target.value } : p))} placeholder="Version (e.g. 1.0)" className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
                              <div>
                                <label className="text-[10px] text-primary-foreground/30 font-body mb-1 block">Display Section</label>
                                <select value={pf.display_section} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, display_section: e.target.value, is_sms_review: e.target.value === "sms_reviews" ? true : p.is_sms_review } : p))} className="w-full px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                                  {DISPLAY_SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                              </div>
                              <label className="flex items-center gap-2 cursor-pointer self-end pb-2">
                                <input type="checkbox" checked={pf.is_sms_review} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, is_sms_review: e.target.checked } : p))} className="rounded border-primary-foreground/20 text-teal focus:ring-teal" />
                                <span className="text-xs text-primary-foreground/50 font-body">SMS Review</span>
                              </label>
                            </div>
                          </>
                        )}

                        {/* Safety Bulletin fields */}
                        {pf.document_type === "safety_bulletin" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <select value={pf.category} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, category: e.target.value } : p))} className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                              {BULLETIN_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <select value={pf.severity} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, severity: e.target.value } : p))} className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                              {BULLETIN_SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                          </div>
                        )}

                        {/* Announcement fields */}
                        {pf.document_type === "announcement" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <select value={pf.priority} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, priority: e.target.value } : p))} className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                              {ANNOUNCEMENT_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                            <label className="flex items-center gap-2 cursor-pointer self-center">
                              <input type="checkbox" checked={pf.is_pinned} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, is_pinned: e.target.checked } : p))} className="rounded border-primary-foreground/20 text-teal focus:ring-teal" />
                              <span className="text-xs text-primary-foreground/50 font-body">Pin this announcement</span>
                            </label>
                          </div>
                        )}

                        {/* Onboarding Material fields */}
                        {pf.document_type === "onboarding_material" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <select value={pf.category} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, category: e.target.value } : p))} className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                              <option value="general">General</option>
                              <option value="safety">Safety</option>
                              <option value="operations">Operations</option>
                              <option value="hr">HR</option>
                              <option value="compliance">Compliance</option>
                            </select>
                          </div>
                        )}

                        {/* Training Document fields */}
                        {pf.document_type === "training_document" && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <select value={pf.required_role} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, required_role: e.target.value } : p))} className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all">
                              {EMPLOYEE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            <input type="text" value={pf.version} onChange={(e) => setOpPendingFiles((prev) => prev.map((p, i) => i === idx ? { ...p, version: e.target.value } : p))} placeholder="Version (e.g. 1.0)" className="px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={opUploading || opPendingFiles.length === 0}
                  className="px-6 py-3 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body disabled:opacity-50"
                >
                  {opUploading ? "Uploading..." : `Upload ${opPendingFiles.length} Document${opPendingFiles.length > 1 ? "s" : ""}`}
                </button>
              </form>
            </div>
          )}

          {opLoading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
          ) : (
            <>
              {activeOpDocs.length === 0 ? (
                <div className="glass-card rounded-2xl p-16 text-center">
                  <FolderOpen className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
                  <p className="text-primary-foreground/40 font-body">No operating documents uploaded yet.</p>
                  <p className="text-primary-foreground/20 font-body text-xs mt-2">Upload SMS, CONOPS, emergency procedures, and other company-wide documents.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(
                    activeOpDocs.reduce<Record<string, typeof activeOpDocs>>((acc, doc) => {
                      const cat = doc.category || "General";
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push(doc);
                      return acc;
                    }, {})
                  )
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([category, docs]) => (
                      <div key={category}>
                        <h3 className="text-xs text-primary-foreground/40 font-body font-bold uppercase tracking-wider mb-3">{category}</h3>
                        <div className="space-y-3">{docs.map((doc) => <OpDocRow key={doc.id} doc={doc} />)}</div>
                      </div>
                    ))}
                </div>
              )}

              <div className="mt-10">
                <button onClick={() => setShowArchive(!showArchive)} className="flex items-center gap-2 text-primary-foreground/40 hover:text-primary-foreground/60 font-body text-sm transition-colors mb-4">
                  <Archive className="w-4 h-4" />
                  Archive ({archivedOpDocs.length})
                  {showArchive ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
                {showArchive && archivedOpDocs.length > 0 && (
                  <div className="space-y-3 opacity-60">{archivedOpDocs.map((doc) => <OpDocRow key={doc.id} doc={doc} showRestore />)}</div>
                )}
                {showArchive && archivedOpDocs.length === 0 && (
                  <p className="text-xs text-primary-foreground/20 font-body ml-6">No archived documents.</p>
                )}
              </div>
            </>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDocuments;
